import { useEffect, useState } from 'react';
import { X, Loader2, FileText, Network, GitBranch, Clock, BookOpen } from 'lucide-react';
import {
  getDocumentIntelligence,
  type DocumentIntelligence,
} from '@/api/knowledgeAtlas';
import { cn } from '@/lib/utils';

interface IntelligencePanelProps {
  documentId: number | null;
  municipalityId: number;
  onClose: () => void;
  /** Fly to a connected document (clicked inside the panel). */
  onSelectDocument: (documentId: number) => void;
}

/**
 * Clean light elevated panel (Linear/Figma style) that slides in from the
 * right when a landmark is selected. It proves Ordinal *understands* the
 * document: chunk + connection counts, most-connected topics, most-connected
 * documents, coverage and recent activity.
 */
export function IntelligencePanel({
  documentId,
  municipalityId,
  onClose,
  onSelectDocument,
}: IntelligencePanelProps) {
  const [data, setData] = useState<DocumentIntelligence | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = documentId !== null;

  useEffect(() => {
    if (documentId === null) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    // Keep the previous data visible briefly so the slide-in feels continuous.

    getDocumentIntelligence(documentId, municipalityId)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load document intelligence');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [documentId, municipalityId]);

  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-y-0 right-0 z-20 flex w-full max-w-[420px] p-4',
        'transition-transform duration-500 ease-out',
        open ? 'translate-x-0' : 'translate-x-[110%]'
      )}
      aria-hidden={!open}
    >
      <div
        className="pointer-events-auto flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white"
        style={{
          boxShadow: '0 16px 48px -20px rgba(15,23,42,0.25)',
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div className="min-w-0">
            <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
              {data?.docType ?? 'Document'}
            </div>
            <h2 className="mt-1 truncate text-lg font-semibold text-slate-900">
              {data?.title ?? (loading ? 'Loading…' : '—')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close intelligence panel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : loading && !data ? (
            <PanelSkeleton />
          ) : data ? (
            <div className="space-y-7">
              {/* Big stats */}
              <div className="grid grid-cols-2 gap-3">
                <Stat
                  icon={<FileText className="h-3.5 w-3.5" />}
                  label="Chunks"
                  value={data.chunkCount.toLocaleString()}
                />
                <Stat
                  icon={<Network className="h-3.5 w-3.5" />}
                  label="Connected docs"
                  value={data.connectedDocuments.length.toLocaleString()}
                />
              </div>

              {/* Topics */}
              {data.connectedTopics.length > 0 && (
                <Section title="Most Connected Topics" icon={<GitBranch className="h-3.5 w-3.5" />}>
                  <div className="flex flex-wrap gap-2">
                    {data.connectedTopics.map((topic) => (
                      <span
                        key={topic}
                        className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </Section>
              )}

              {/* Connected documents */}
              {data.connectedDocuments.length > 0 && (
                <Section title="Most Connected Documents" icon={<Network className="h-3.5 w-3.5" />}>
                  <ul className="space-y-1">
                    {data.connectedDocuments.map((doc) => (
                      <li key={doc.id}>
                        <button
                          type="button"
                          onClick={() => onSelectDocument(doc.id)}
                          className="group flex w-full items-start justify-between gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-slate-100"
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-sm text-slate-700 group-hover:text-slate-900">
                              {doc.title}
                            </span>
                            {doc.rationale ? (
                              // Prefer the written sentence over the term list.
                              <span className="mt-0.5 block text-[11px] text-slate-400">
                                {doc.rationale}
                              </span>
                            ) : (
                              doc.sharedTerms &&
                              doc.sharedTerms.length > 0 && (
                                <span className="mt-0.5 block truncate text-[11px] text-slate-400">
                                  shared: {doc.sharedTerms.join(', ')}
                                </span>
                              )
                            )}
                          </span>
                          <span className="mt-0.5 shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                            {formatWeight(doc.weight)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* Coverage + activity */}
              <div className="space-y-4 border-t border-slate-200 pt-5">
                <MetaRow
                  icon={<BookOpen className="h-3.5 w-3.5" />}
                  label="Knowledge Coverage"
                  value={data.coverage}
                />
                <MetaRow
                  icon={<Clock className="h-3.5 w-3.5" />}
                  label="Recent Activity"
                  value={data.recentActivity}
                />
              </div>

              {loading && (
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Loader2 className="h-3 w-3 animate-spin" /> Updating…
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-slate-400">
        {icon}
        {label}
      </div>
      <div className="mt-1.5 text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function MetaRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-slate-400">{icon}</div>
      <div>
        <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{label}</div>
        <div className="mt-0.5 text-sm text-slate-600">{value}</div>
      </div>
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="animate-pulse space-y-7">
      <div className="grid grid-cols-2 gap-3">
        <div className="h-20 rounded-xl bg-slate-100" />
        <div className="h-20 rounded-xl bg-slate-100" />
      </div>
      <div className="space-y-3">
        <div className="h-3 w-32 rounded bg-slate-100" />
        <div className="flex gap-2">
          <div className="h-6 w-20 rounded-full bg-slate-100" />
          <div className="h-6 w-16 rounded-full bg-slate-100" />
          <div className="h-6 w-24 rounded-full bg-slate-100" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-3 w-40 rounded bg-slate-100" />
        <div className="h-9 rounded-lg bg-slate-100" />
        <div className="h-9 rounded-lg bg-slate-100" />
        <div className="h-9 rounded-lg bg-slate-100" />
      </div>
    </div>
  );
}

function formatWeight(weight: number): string {
  if (weight >= 1) return weight.toFixed(0);
  return `${Math.round(weight * 100)}%`;
}
